import { MantineProvider } from "@mantine/core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { i18n } from "i18next";
import { useMemo } from "react";
import { I18nextProvider } from "react-i18next";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AccessHistoryPage } from "../features/access/AccessHistoryPage";
import { AccountDeletedPage } from "../features/account/AccountDeletedPage";
import { AccountPage } from "../features/account/AccountPage";
import { SuspendedPage } from "../features/account/SuspendedPage";
import { AdminAccountsPage } from "../features/admin/AdminAccountsPage";
import { AdminApprovalsPage } from "../features/admin/AdminApprovalsPage";
import { AdminAuditPage } from "../features/admin/AdminAuditPage";
import { AdminBillingPage } from "../features/admin/AdminBillingPage";
import { AdminCompaniesPage } from "../features/admin/AdminCompaniesPage";
import { AdminCreditPage } from "../features/admin/AdminCreditPage";
import { AdminDeadLettersPage } from "../features/admin/AdminDeadLettersPage";
import { AdminNetworksPage } from "../features/admin/AdminNetworksPage";
import { AdminOutboxPage } from "../features/admin/AdminOutboxPage";
import { AdminPaymentsPage } from "../features/admin/AdminPaymentsPage";
import { AdminProvidersPage } from "../features/admin/AdminProvidersPage";
import { AdminSettingsPage } from "../features/admin/AdminSettingsPage";
import { AdminTestimonialsPage } from "../features/admin/AdminTestimonialsPage";
import { AdminVerificationPage } from "../features/admin/AdminVerificationPage";
import { ApprovalsPage } from "../features/approvals/ApprovalsPage";
import { ForgotPasswordPage } from "../features/auth/ForgotPasswordPage";
import { OnboardingPage } from "../features/auth/OnboardingPage";
import { RecoverCodePage } from "../features/auth/RecoverCodePage";
import { ResetPasswordPage } from "../features/auth/ResetPasswordPage";
import { NotMePage } from "../features/security/NotMePage";
import { ResumeOnboardingPage } from "../features/auth/ResumeOnboardingPage";
import { RetryVerificationPage } from "../features/auth/RetryVerificationPage";
import { SignInPage } from "../features/auth/SignInPage";
import { SignUpPage } from "../features/auth/SignUpPage";
import { VerifyEmailPage } from "../features/auth/VerifyEmailPage";
import { AcceptInvitePage } from "../features/business/AcceptInvitePage";
import { BusinessPage } from "../features/business/BusinessPage";
import { CompanyPage } from "../features/business/CompanyPage";
import { ConsentsPage } from "../features/consents/ConsentsPage";
import { DevelopersPage } from "../features/developers/DevelopersPage";
import { LandingPage } from "../features/landing/LandingPage";
import { NotFoundPage } from "../features/notfound/NotFoundPage";
import { PrivacyPage, TermsPage } from "../features/public/LegalPage";
import { PricingPage } from "../features/public/PricingPage";
import { ReceiptDetailPage } from "../features/receipts/ReceiptDetailPage";
import { ReceiptsPage } from "../features/receipts/ReceiptsPage";
import { VerifyReceiptPage } from "../features/receipts/VerifyReceiptPage";
import { SecurityPage } from "../features/security/SecurityPage";
import { SetupAuthenticatorPage } from "../features/security/SetupAuthenticatorPage";
import { SETUP_AUTHENTICATOR_PATH } from "../shared/session/totp";
import { BrandProvider, type PublicBrand } from "../shared/brand/brand";
import { buildTheme, cssVariables } from "../shared/brand/theme";
import { Shell } from "../shared/layout/Shell";
import { retryable } from "../shared/session/errors";
import { SessionProvider } from "../shared/session/session";
import { StepUpProvider } from "../shared/session/stepup";

export function AppRoutes() {
  return (
    <Shell>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/onboarding/resume" element={<ResumeOnboardingPage />} />
        <Route path="/onboarding/retry" element={<RetryVerificationPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/security/not-me" element={<NotMePage />} />
        <Route path="/recover-code" element={<RecoverCodePage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/suspended" element={<SuspendedPage />} />
        <Route path="/account-deleted" element={<AccountDeletedPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/developers" element={<DevelopersPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/consents" element={<ConsentsPage />} />
        <Route path="/approvals" element={<ApprovalsPage />} />
        <Route path="/access-history" element={<AccessHistoryPage />} />
        <Route path="/receipts" element={<ReceiptsPage />} />
        <Route path="/receipts/:id" element={<ReceiptDetailPage />} />
        <Route path="/verify-receipt" element={<VerifyReceiptPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path={SETUP_AUTHENTICATOR_PATH} element={<SetupAuthenticatorPage />} />
        <Route path="/business" element={<BusinessPage />} />
        <Route path="/business/:companyId/*" element={<CompanyPage />} />
        <Route path="/invitations/accept" element={<AcceptInvitePage />} />
        <Route path="/admin" element={<Navigate to="/admin/companies" replace />} />
        <Route path="/admin/companies" element={<AdminCompaniesPage />} />
        <Route path="/admin/settings" element={<AdminSettingsPage />} />
        <Route path="/admin/approvals" element={<AdminApprovalsPage />} />
        <Route path="/admin/providers" element={<AdminProvidersPage />} />
        <Route path="/admin/billing" element={<AdminBillingPage />} />
        <Route path="/admin/trusted-networks" element={<AdminNetworksPage />} />
        <Route path="/admin/verification" element={<AdminVerificationPage />} />
        <Route path="/admin/webhook-deliveries" element={<AdminDeadLettersPage />} />
        <Route path="/admin/payments" element={<AdminPaymentsPage />} />
        <Route path="/admin/credit-adjustments" element={<AdminCreditPage />} />
        <Route path="/admin/accounts" element={<AdminAccountsPage />} />
        <Route path="/admin/outbox" element={<AdminOutboxPage />} />
        <Route path="/admin/audit-events" element={<AdminAuditPage />} />
        <Route path="/admin/testimonials" element={<AdminTestimonialsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Shell>
  );
}

export function Providers({ brand, i18n, children }: { brand: PublicBrand; i18n: i18n; children: React.ReactNode }) {
  const theme = useMemo(() => buildTheme(brand), [brand]);
  const resolver = useMemo(() => cssVariables(brand), [brand]);
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: (n, error) => n < 1 && retryable(error), refetchOnWindowFocus: false },
        },
      }),
    [],
  );
  return (
    <BrandProvider brand={brand}>
      <I18nextProvider i18n={i18n}>
        <MantineProvider theme={theme} cssVariablesResolver={resolver} defaultColorScheme="auto">
          <QueryClientProvider client={queryClient}>
            <SessionProvider>
              <StepUpProvider>{children}</StepUpProvider>
            </SessionProvider>
          </QueryClientProvider>
        </MantineProvider>
      </I18nextProvider>
    </BrandProvider>
  );
}

export function App({ brand, i18n }: { brand: PublicBrand; i18n: i18n }) {
  return (
    <Providers brand={brand} i18n={i18n}>
      <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, "")}>
        <AppRoutes />
      </BrowserRouter>
    </Providers>
  );
}
